import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { getMetricScore, availableIndicatorCount } from './metricConfig';
import { reportMetrics, overallReportScore } from '../../utils/buildReportPayload';
import { MeasurementResults } from '../../hooks/useResultsExport';
import MetricGauge, { remapForTwoBands } from './MetricGauge';
import { markerPosition } from './scoreBands';
import { useTheme } from 'styled-components';
import { colors, fontSize, fontWeight, spacing } from '../../style/tokens';
import { CE_GROUPS, isIndicatorEnabled } from './ceIndicatorGroups';
import CeVitalGaugeRow from './CeVitalGaugeRow';
import { contentKey, VitalSignsContentMap } from '../../api/vitalSignsContent';
import { Sex, SmokingStatus } from '@biosensesignal/web-sdk';

interface Props {
  results: MeasurementResults;
  /**
   * CE only: code → content map from the vital-signs content API, pre-fetched by
   * the caller so this off-screen view renders synchronously. When present, each
   * CE indicator shows its description block under the bar (mirroring the non-CE
   * per-metric layout). Null/absent → bars only.
   */
  contentMap?: VitalSignsContentMap | null;
}

// Returns '#1a1a1a' or '#ffffff' depending on perceived luminance of a hex colour
function contrastColor(hex: string): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#1a1a1a';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 128 ? '#1a1a1a' : '#ffffff';
}

// ── Layout ────────────────────────────────────────────────────────────────────
// 720px wide ≈ 190mm at 96dpi, matching A4 with 10mm margins — text renders at
// correct print pt sizes (14px CSS ≈ 10.5pt, 18px ≈ 13.5pt).

const Root = styled.div`
  width: 720px;
  background: #ffffff;
  font-family: 'Outfit', sans-serif;
`;

// ── Header ────────────────────────────────────────────────────────────────────

const Header = styled.div<{ bg: string; fg: string }>`
  padding: 28px 28px 22px;
  background: ${({ bg }) => bg};
  border-bottom: 3px solid;
  border-image: linear-gradient(to right, currentColor, transparent) 1;
`;

const Title = styled.h1<{ fg: string }>`
  font-size: 26px;
  font-weight: ${fontWeight.extrabold};
  color: ${({ fg }) => fg};
  margin: 0 0 6px;
`;

const DateLine = styled.p<{ fg: string }>`
  font-size: ${fontSize.md};
  color: ${({ fg }) => fg};
  opacity: 0.7;
  margin: 0;
`;

// ── Biometrics block (CE) ───────────────────────────────────────────────────

const BioBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 26px;
  padding: 12px 28px 14px;
  background: #fff;
  border-bottom: 1px solid #eee;
`;

const BioItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const BioLabel = styled.span`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const BioValue = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${colors.text};
`;

// ── Overall score bar ─────────────────────────────────────────────────────────

const ScoreSection = styled.div`
  padding: 18px 28px 14px;
  background: #fff;
`;

// ── Table of contents ─────────────────────────────────────────────────────────

const TocSection = styled.div`
  padding: 18px 28px 0;
  background: #fff;
  border-bottom: 2px solid #eee;
`;

const TocHeading = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.gradient1};
  margin-bottom: 10px;
`;

const TocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: ${spacing.xl} 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
`;

const TocIcon = styled.div`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${fontSize.xl2};
`;

const TocName = styled.span`
  font-size: 15px;
  font-weight: ${fontWeight.semibold};
  color: ${({ theme }) => theme.gradient1};
  white-space: nowrap;
`;

const TocFill = styled.span`
  flex: 1;
  border-bottom: 1.5px dotted ${({ theme }) => theme.gradient1};
  opacity: 0.3;
  margin-bottom: 2px;
`;

const TocPage = styled.span`
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.gradient1};
  white-space: nowrap;
`;

const ScoreLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  color: #555;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const ScoreValue = styled.div`
  font-size: 15px;
  font-weight: ${fontWeight.bold};
  color: #1a1a1a;
  margin-bottom: 10px;
`;

const ScoreTrack = styled.div<{ $bands: 2 | 3 }>`
  position: relative;
  height: 10px;
  border-radius: 6px;
  background: ${({ $bands }) => $bands === 2
    ? 'linear-gradient(to right, #fde04f 0%, #fde04f 50%, #8ecf3a 50%, #8ecf3a 100%)'
    : 'linear-gradient(to right, #ff7043, #fdd835, #7ec820)'};
`;

const ScoreMarker = styled.div<{ left: number }>`
  position: absolute;
  top: 50%;
  left: ${({ left }) => left}%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 2.5px solid #333;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
`;

const ScoreLegends = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 7px;
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: #555;
`;

const ScoreCount = styled.div`
  margin-top: 6px;
  font-size: 11px;
  color: #777;
  text-align: center;
`;

// ── Metric section ────────────────────────────────────────────────────────────

const MetricSection = styled.div`
  border-bottom: 1px solid #eee;
  border-top: 3px solid ${({ theme }) => theme.gradient1};
  margin-top: 2px;
`;

const MetricHeader = styled.div`
  padding: 14px 28px 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const MetricLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MetricIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const MetricName = styled.h2`
  font-size: 24px;
  font-weight: ${fontWeight.extrabold};
  color: ${({ theme }) => theme.gradient1};
  margin: 0;
`;

const MetricStatus = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: #555;
  white-space: nowrap;
`;

const GaugeWrap = styled.div`
  padding: 10px 28px 12px;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
`;

// ── Info blocks ───────────────────────────────────────────────────────────────

const InfoBlocks = styled.div`
  padding: ${spacing.md} ${spacing.xl3} ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
`;

const InfoBlock = styled.div<{ variant?: 'advice' }>`
  background: #fff;
  border: 1.5px solid ${({ theme }) => theme.gradient1};
  border-radius: 12px;
  padding: ${spacing.md} ${spacing.lg};
`;

const BlockLabel = styled.div`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.gradient1};
  margin-bottom: 7px;
  line-height: 1.2;
`;

const BlockText = styled.p`
  font-size: ${fontSize.base};
  color: ${colors.text};
  line-height: 1.6;
  margin: 0;
`;

const BlockList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BlockListItem = styled.li`
  font-size: ${fontSize.base};
  color: ${colors.text};
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.gradient1};
    flex-shrink: 0;
  }
`;

// ── CE groups ─────────────────────────────────────────────────────────────────

// One section per indicator, marked no-break so the bar + its description stay
// together; the PDF pager breaks *between* sections and only pushes one to the
// next page when it won't fit — so a bar is never orphaned from its text, while
// short indicators still share a page.
const CeIndicatorSection = styled.div`
  background: #fff;
  border-top: 3px solid ${({ theme }) => theme.gradient1};
`;

const CeGroupTitle = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.gradient1};
  padding: 14px 28px 6px;
`;

const CeIndicatorWrap = styled.div`
  padding: 0 28px;
`;

// Description rendered as flowing document text (not a bordered box) so it can
// split across pages cleanly. Emitted one paragraph per block.
const CeDescription = styled.div`
  padding: 6px 28px 12px;
`;

const CeLowConfNote = styled.p`
  font-size: ${fontSize.xs};
  color: #888;
  line-height: 1.4;
  margin: 4px 28px 12px;
`;

// ── Disclaimer ────────────────────────────────────────────────────────────────

// Per-indicator disclaimer shown under each description block, mirroring the
// app's MetricDetailPage (metric.ui.disclaimer).
const MetricDisclaimer = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  line-height: 1.5;
  margin: 2px 2px 0;
`;

const FooterDisclaimer = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  text-align: center;
  padding: ${spacing.xl} ${spacing.xl3} ${spacing.xl3};
  line-height: 1.55;
  margin: 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

const ReportPdfView: React.FC<Props> = ({ results, contentMap }) => {
  const intl = useIntl();
  const { gradient1, sfondo, metricIcons, resultBands, istanza_ce, enabledMetrics } = useTheme();
  const dem = results.demographics;

  const headerBg = sfondo || colors.bg;
  const headerFg = contrastColor(headerBg);

  if (istanza_ce) {
    return (
      <Root>
        <Header bg={headerBg} fg={headerFg}>
          <Title fg={headerFg} style={{ fontSize: '20px', textAlign: 'center' }}>
            {intl.formatMessage({ id: 'report.measurementResults' })}
          </Title>
          <DateLine fg={headerFg} style={{ textAlign: 'center' }}>
            {results.sessionInfo.measurementDate} · {results.sessionInfo.measurementTime}
          </DateLine>
        </Header>

        {dem && (
          <BioBlock data-pdf-nobreak="true">
            <BioItem>
              <BioLabel>{intl.formatMessage({ id: 'form.sex' })}</BioLabel>
              <BioValue>{intl.formatMessage({ id: dem.sex === Sex.MALE ? 'form.male' : 'form.female' })}</BioValue>
            </BioItem>
            <BioItem>
              <BioLabel>{intl.formatMessage({ id: 'form.height' })}</BioLabel>
              <BioValue>{dem.height}</BioValue>
            </BioItem>
            <BioItem>
              <BioLabel>{intl.formatMessage({ id: 'form.weight' })}</BioLabel>
              <BioValue>{dem.weight}</BioValue>
            </BioItem>
            <BioItem>
              <BioLabel>{intl.formatMessage({ id: 'form.age' })}</BioLabel>
              <BioValue>{dem.age}</BioValue>
            </BioItem>
            <BioItem>
              <BioLabel>{intl.formatMessage({ id: 'form.smokingStatus' })}</BioLabel>
              <BioValue>{intl.formatMessage({ id: dem.smokingStatus === SmokingStatus.SMOKER ? 'form.smoker' : 'form.nonSmoker' })}</BioValue>
            </BioItem>
          </BioBlock>
        )}

        {(() => {
          const vs = results.vitalSigns;
          // Flatten enabled indicators across groups. License-disabled ones are
          // never produced → dropped; a group left with none contributes nothing.
          // Each surviving indicator becomes its own page-broken section so the
          // bar + description stay together on one page.
          const rows: Array<{
            group: typeof CE_GROUPS[number];
            ind: typeof CE_GROUPS[number]['indicators'][number];
            groupFirst: boolean;
          }> = [];
          for (const group of CE_GROUPS) {
            const enabled = group.indicators.filter(ind =>
              isIndicatorEnabled(ind, results.enabled),
            );
            enabled.forEach((ind, i) =>
              rows.push({ group, ind, groupFirst: i === 0 }),
            );
          }

          return rows.map(({ group, ind, groupFirst }) => {
            const { display } = ind.getValue(vs);
            const score = ind.getScore ? ind.getScore(vs, dem) : 50;
            const available = ind.isAvailable(vs);
            const lowConfidence = available && !!ind.isLowConfidence?.(vs);
            // Description from the content API (pre-fetched by the caller),
            // matched by resultCode — mirrors the indicator detail page. Split
            // into paragraphs so a long description can span pages while breaking
            // only between paragraphs (never mid-line, never an oversized zone).
            const description = contentMap?.get(contentKey(ind.resultCode))?.description || '';
            const paras = description
              ? description.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean)
              : [];
            // Each section is tagged as an indicator; generatePdf enforces "at
            // most two per page, and a second only if the first stays within the
            // page's top half" from the measured heights.
            return (
              <CeIndicatorSection key={ind.labelKey} data-pdf-indicator="true">
                {/* Bar + description heading + lead paragraph form ONE no-break
                    unit, so a bar is never split from the start of its
                    description across a page boundary. */}
                <div data-pdf-nobreak="true">
                  {groupFirst && (
                    <CeGroupTitle>{intl.formatMessage({ id: group.titleKey })}</CeGroupTitle>
                  )}
                  <CeIndicatorWrap>
                    <CeVitalGaugeRow
                      label={intl.formatMessage({ id: ind.labelKey })}
                      value={display}
                      score={score}
                      bands={group.bands}
                      hideValue={ind.hideValue}
                      unavailable={!available}
                      lowConfidence={lowConfidence}
                      legend={ind.legend}
                      invertGaugeLabels={ind.invertGaugeLabels}
                    />
                  </CeIndicatorWrap>
                  {paras.length > 0 && (
                    <CeDescription>
                      <BlockLabel>{intl.formatMessage({ id: 'metric.ui.description' })}</BlockLabel>
                      <BlockText style={{ whiteSpace: 'pre-line' }}>{paras[0]}</BlockText>
                    </CeDescription>
                  )}
                </div>
                {/* Remaining paragraphs each get their own no-break block, so the
                    description may run onto further pages, breaking between
                    paragraphs. */}
                {paras.slice(1).map((para, i) => (
                  <CeDescription key={i} data-pdf-nobreak="true" style={{ paddingTop: 0 }}>
                    <BlockText style={{ whiteSpace: 'pre-line' }}>{para}</BlockText>
                  </CeDescription>
                ))}
                {lowConfidence && (
                  <CeLowConfNote>{intl.formatMessage({ id: 'ce.lowConfidenceNote' })}</CeLowConfNote>
                )}
              </CeIndicatorSection>
            );
          });
        })()}
      </Root>
    );
  }

  // Same filter as the report page and the partner JSON payload, so a partner
  // that narrows enabled_metrics gets a PDF listing exactly what's on screen.
  const visibleMetrics = reportMetrics(enabledMetrics);
  // A partner left with a single indicator (the Burnout-only case) gets a
  // one-page PDF, mirroring the report page. The contents list would hold one
  // row pointing at a page that only exists because of the row, and the overall
  // score averages snapshot indicators this report never shows — so both go,
  // and the indicator no longer forces a page break, leaving header + indicator
  // on page 1.
  const singleMetric = visibleMetrics.length === 1;
  const avgScore = overallReportScore(results);
  const remappedAvg = resultBands === 2 ? remapForTwoBands(avgScore) : avgScore;
  const markerLeft = Math.max(4, Math.min(96, remappedAvg));

  return (
    <Root>
      <Header bg={headerBg} fg={headerFg}>
        <Title fg={headerFg}>{intl.formatMessage({ id: 'report.wellnessSnapshot' })}</Title>
        <DateLine fg={headerFg}>
          {results.sessionInfo.measurementDate} · {results.sessionInfo.measurementTime}
        </DateLine>
      </Header>

      {!singleMetric && (
        <ScoreSection>
          <ScoreLabel>{intl.formatMessage({ id: 'report.overallWellnessScore' })}</ScoreLabel>
          <ScoreTrack $bands={resultBands}>
            <ScoreMarker left={markerLeft} />
          </ScoreTrack>
          <ScoreLegends>
            {resultBands === 3 && <span>{intl.formatMessage({ id: 'metric.gauge.outsideRange' })}</span>}
            <span>{intl.formatMessage({ id: 'metric.gauge.worthNoting' })}</span>
            <span>{intl.formatMessage({ id: 'metric.gauge.normal' })}</span>
          </ScoreLegends>
          <ScoreCount>
            {intl.formatMessage({ id: 'report.indicatorsUsed' }, availableIndicatorCount(results, dem))}
          </ScoreCount>
        </ScoreSection>
      )}

      {!singleMetric && (
        <TocSection>
          <TocHeading>{intl.formatMessage({ id: 'report.contents' })}</TocHeading>
          {visibleMetrics.map((config, i) => (
            <TocRow key={config.id}>
              <TocIcon>
                {(metricIcons[config.id] || config.icon)
                  ? <img src={metricIcons[config.id] || config.icon} alt="" style={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
                  : config.emoji}
              </TocIcon>
              <TocName>{intl.formatMessage({ id: config.labelKey })}</TocName>
              <TocFill />
              <TocPage>p. {i + 2}</TocPage>
            </TocRow>
          ))}
        </TocSection>
      )}

      {visibleMetrics.map((config) => {
        const score = getMetricScore(config, results, dem);
        const available = score !== null;
        // §4.5/§4.9 band on a raw value, not the score (Rule 1) — null elsewhere.
        const rawValue = config.getBandValue?.(results, dem) ?? null;
        const { key: interpKey, values: interpValues } = config.getInterpretation(results, score ?? 0, dem);
        const iconUrl = metricIcons[config.id] || config.icon;

        // Protocol v2.0: mirror MetricDetailPage — indicators with a descriptionKey
        // render a single "Description" block; the rest keep the legacy 4-block layout.
        const blocks = config.descriptionKey
          ? [
              { key: 'metric.ui.description', text: intl.formatMessage({ id: config.descriptionKey }) },
            ]
          : [
              { key: 'metric.ui.explanation', text: intl.formatMessage({ id: config.explanationKey }) },
              { key: 'metric.ui.interpretation', text: intl.formatMessage({ id: interpKey }, interpValues) },
              { key: 'metric.ui.signalsAnalysed', text: intl.formatMessage({ id: config.signalKey }) },
              { key: 'metric.ui.advice', text: intl.formatMessage({ id: config.adviceKey }) },
            ];

        return (
          <MetricSection
            key={config.id}
            data-pdf-pagebreak={singleMetric ? undefined : 'true'}
          >
            <div data-pdf-nobreak="true">
              <MetricHeader>
                <MetricLeft>
                  <MetricIcon>
                    {iconUrl
                      ? <img src={iconUrl} alt="" style={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
                      : config.emoji}
                  </MetricIcon>
                  <MetricName>{intl.formatMessage({ id: config.labelKey })}</MetricName>
                </MetricLeft>
                <MetricStatus>
                  {available
                    ? intl.formatMessage({ id: config.getDescriptionKey(score!, rawValue) })
                    : intl.formatMessage({ id: 'metric.ui.unavailable' })}
                </MetricStatus>
              </MetricHeader>

              {available && (
                <GaugeWrap>
                  <MetricGauge
                    position={markerPosition(config.id, score!, resultBands, rawValue)}
                    labels={config.gaugeKeys.map(k => intl.formatMessage({ id: k })) as [string, string, string]}
                    size="small"
                    labelColor="#444"
                    labelFontSize="11px"
                    bands={resultBands}
                  />
                </GaugeWrap>
              )}
            </div>

            <InfoBlocks>
              {blocks.map(block => (
                <InfoBlock key={block.key} data-pdf-nobreak="true">
                  <BlockLabel>{intl.formatMessage({ id: block.key })}</BlockLabel>
                  <BlockText>{block.text}</BlockText>
                </InfoBlock>
              ))}
              <MetricDisclaimer data-pdf-nobreak="true">
                {intl.formatMessage({ id: 'metric.ui.disclaimer' })}
              </MetricDisclaimer>
            </InfoBlocks>
          </MetricSection>
        );
      })}

      {/* With one indicator the footer sits a few lines under that indicator's
          own metric.ui.disclaimer and repeats its first sentence — and the
          per-indicator line is the fuller of the two (it adds the "consult a
          professional" sentence), so the footer is what goes. */}
      {!singleMetric && (
        <FooterDisclaimer>
          {intl.formatMessage({ id: 'report.reportDisclaimer' })}
        </FooterDisclaimer>
      )}
    </Root>
  );
};

export default ReportPdfView;
